import React from 'react';

// Import config and utils
import { useIntl } from '../../util/reactIntl';
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_YOUTUBE,
} from '../../util/types';
import {
  required,
  nonEmptyArray,
  validateInteger,
  validateYoutubeURL,
} from '../../util/validators';
// Import shared components
import { FieldCheckboxGroup, FieldSelect, FieldTextInput, FieldBoolean } from '../../components';
import FieldSectorAutocomplete from '../FieldSectorAutocomplete/FieldSectorAutocomplete';
import FieldProvinciasSelect from '../FieldProvinciasSelect/FieldProvinciasSelect';
import { empleadosTramos } from '../../config/configFilters';
// Import modules from this directory
import css from './CustomExtendedDataField.module.css';

const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));

const getLabel = fieldConfig => fieldConfig?.saveConfig?.label || fieldConfig?.label;

const CustomFieldEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { enumOptions = [], saveConfig, key } = fieldConfig || {};
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({
      id: 'CustomExtendedDataField.placeholderSingleSelect',
    });
  const filterOptions = createFilterOptions(enumOptions);

  const label = getLabel(fieldConfig);

  // SECTOR-AUTOCOMPLETE-01: el sector usa un combobox custom que
  // soporta la jerarquía 11 madres + 55 subsectores con buscador
  // fuzzy y sinónimos. Se sustituye al <FieldSelect> sólo para
  // este key — el resto de enums siguen el flujo nativo.
  if (key === 'sector') {
    return (
      <FieldSectorAutocomplete
        name={name}
        allowParents={props.listingType === 'mandato-compra'}
        label={label}
        helpText={fieldConfig?.helpText}
        validate={value => {
          const known = enumOptions.some(
            option =>
              (option.option === value &&
                (props.listingType === 'mandato-compra' || !option.suboptions?.length)) ||
              option.suboptions?.some(child => child.option === value)
          );
          return value && !known ? defaultRequiredMessage : validateMaybe.validate?.(value);
        }}
      />
    );
  }

  return filterOptions ? (
    <FieldSelect
      className={css.customField}
      name={name}
      id={formId ? `${formId}.${name}` : name}
      label={label}
      helpText={fieldConfig?.helpText}
      {...validateMaybe}
    >
      <option disabled value="">
        {placeholder}
      </option>
      {filterOptions.map(optionConfig => {
        const key = optionConfig.key;
        return (
          <option key={key} value={key}>
            {optionConfig.label}
          </option>
        );
      })}
    </FieldSelect>
  ) : null;
};

const CustomFieldMultiEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { enumOptions = [], saveConfig, key } = fieldConfig || {};
  const { isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: nonEmptyArray(requiredMessage || defaultRequiredMessage) }
    : {};

  // FASE2-WIZARD-2b: provincias usa el MISMO combobox que el filtro del
  // buscador, no 53 casillas. Mismo patrón de caso-especial-por-key que
  // `sector` arriba; el resto de multi-enums sigue el flujo nativo.
  if (key === 'provincias') {
    return (
      <FieldProvinciasSelect
        name={name}
        label={label}
        options={createFilterOptions(enumOptions).map(o => ({
          value: o.key,
          label: o.label,
        }))}
        placeholder={intl.formatMessage({
          id: 'FieldProvinciasSelect.placeholder',
        })}
        searchPlaceholder={intl.formatMessage({
          id: 'FieldProvinciasSelect.searchPlaceholder',
        })}
        emptyLabel={intl.formatMessage({ id: 'FieldProvinciasSelect.empty' })}
        clearLabel={intl.formatMessage({ id: 'FieldProvinciasSelect.clear' })}
        doneLabel={intl.formatMessage({ id: 'FieldProvinciasSelect.done' })}
        helpText={intl.formatMessage({ id: 'FieldProvinciasSelect.help' })}
        maxNotice={intl.formatMessage({
          id: 'FieldProvinciasSelect.maxReached',
        })}
        validate={validateMaybe.validate}
      />
    );
  }

  return enumOptions ? (
    <FieldCheckboxGroup
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      helpText={fieldConfig?.helpText}
      options={createFilterOptions(enumOptions)}
      {...validateMaybe}
    />
  ) : null;
};

const CustomFieldText = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderText' });

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="textarea"
      label={label}
      helpText={fieldConfig?.helpText}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldLong = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { minimum, maximum, saveConfig, key } = fieldConfig;
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });

  // FACTURACION-IDX-01 (3b): campo calculado (derivado de facturacion
  // en el submit del panel); nunca se muestra en el wizard.
  // FASE2-SCHEMA-01.2: empleadosIdx sigue el mismo patrón (derivado del
  // tramo de empleados elegido).
  // FASE2-SCHEMA-01.3: antiguedadIdx, ídem (derivado del tramo de
  // antigüedad elegido).
  if (key === 'facturacionIdx' || key === 'empleadosIdx' || key === 'antiguedadIdx') {
    return null;
  }

  // EMPLEADOS-TRAMOS-01: empleados se edita como select de tramos
  // (EMPLEADOS_OPTIONS, la misma fuente que el filtro de SearchPage).
  // El value de cada opción es el límite inferior del tramo; el panel lo
  // persiste como number (pub_empleados sigue siendo long). Mismo patrón
  // de caso-especial-por-key que `sector` en CustomFieldEnum.
  if (key === 'empleados') {
    const validateMaybe = isRequired
      ? { validate: required(requiredMessage || defaultRequiredMessage) }
      : {};
    return (
      <FieldSelect
        className={css.customField}
        name={name}
        id={formId ? `${formId}.${name}` : name}
        label={label}
        helpText={fieldConfig?.helpText}
        {...validateMaybe}
      >
        <option disabled value="">
          {placeholder}
        </option>
        {empleadosTramos().map(({ lowerBound, label: tramoLabel }) => (
          <option key={lowerBound} value={`${lowerBound}`}>
            {tramoLabel}
          </option>
        ))}
      </FieldSelect>
    );
  }
  // DATAMODEL-01: facturacion pasó a enum y ebitda desapareció — ya
  // no quedan long fields con formato de moneda en el editor.
  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: minimum }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: maximum }
  );

  // Field with schema type 'long' will always be validated against min & max
  const validate = (value, min, max) => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateInteger(value, max, min, numberTooSmallMessage, numberTooBigMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="number"
      step="1"
      helpText={fieldConfig?.helpText}
      parse={value => {
        const parsed = Number.parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      }}
      label={label}
      placeholder={placeholder}
      validate={value => validate(value, minimum, maximum)}
      onWheel={e => {
        // fix: number input should not change value on scroll
        if (e.target === document.activeElement) {
          // Prevent the input value change, because we prefer page scrolling
          e.target.blur();

          // Refocus immediately, on the next tick (after the current function is done)
          setTimeout(() => {
            e.target.focus();
          }, 0);
        }
      }}
    />
  );
};

const CustomFieldBoolean = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderBoolean' });

  return (
    <FieldBoolean
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      helpText={fieldConfig?.helpText}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldYoutube = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({
      id: 'CustomExtendedDataField.placeholderYoutubeVideoURL',
    });

  const notValidUrlMessage = intl.formatMessage({
    id: 'CustomExtendedDataField.notValidYoutubeVideoURL',
  });

  const validate = value => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateYoutubeURL(value, notValidUrlMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={label}
      helpText={fieldConfig?.helpText}
      placeholder={placeholder}
      validate={value => validate(value)}
    />
  );
};

/**
 * Return Final Form field for each configuration according to schema type.
 *
 * These custom extended data fields are for generating input fields from configuration defined
 * in marketplace-custom-config.js. Other panels in EditListingWizard might add more extended data
 * fields (e.g. shipping fee), but these are independently customizable.
 *
 * @param {Object} props should contain fieldConfig that defines schemaType, enumOptions?, and
 * saveConfig for the field.
 */
const CustomExtendedDataField = props => {
  const intl = useIntl();
  const { enumOptions = [], schemaType } = props?.fieldConfig || {};
  const defaultRequiredMessage = intl.formatMessage({
    id: 'CustomExtendedDataField.required',
  });
  const renderFieldComponent = (FieldComponent, props) => (
    <FieldComponent {...props} defaultRequiredMessage={defaultRequiredMessage} intl={intl} />
  );

  return schemaType === SCHEMA_TYPE_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldEnum, props)
    : schemaType === SCHEMA_TYPE_MULTI_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldMultiEnum, props)
    : schemaType === SCHEMA_TYPE_TEXT
    ? renderFieldComponent(CustomFieldText, props)
    : schemaType === SCHEMA_TYPE_LONG
    ? renderFieldComponent(CustomFieldLong, props)
    : schemaType === SCHEMA_TYPE_BOOLEAN
    ? renderFieldComponent(CustomFieldBoolean, props)
    : schemaType === SCHEMA_TYPE_YOUTUBE
    ? renderFieldComponent(CustomFieldYoutube, props)
    : null;
};

export default CustomExtendedDataField;
